/**
 * The renderer side of `azure-storage:named-shares` (plan §3.2, Task 7.4b): main verifies and owns the ONE named set of
 * the live connection; after every successful answer the connection record persists exactly the answer's `shares`
 * (never a draft of its own) and — when the answer can have moved the File Shares folder between the "Open share by
 * name…" action-row state and the named listing — `azure-storage:account-info` is re-read into
 * `storageAccountInfoStore`, which the tree's folder rule reads, so the folder switches without a reconnect.
 * Open by name and Remove fire `feature:storage_share_action` (enum properties only — never a share name).
 */
import { v4 as uuidv4 } from 'uuid';
import type { StorageIpcResult, StorageNamedSharesAction, StorageNamedSharesAnswer, StorageOperationError } from '../../../../types/azureStorage';
import { useConnectionStore } from '../../../connections/store/connectionStore';
import { reprobeStorageAccountInfo } from '../../../database-objects/hooks/useStorageAccountInfoSync';
import { useStorageAccountInfoStore } from '../../../database-objects/store/storageAccountInfoStore';
import { trackStorageEvent } from '../services/storageAnalytics';

export type ShareActionSource = 'toolbar' | 'tree' | 'row-menu' | 'empty-state' | 'panel';
type NamedSharesResult = StorageIpcResult<StorageNamedSharesAnswer>;

const NAMED_SHARES_TIMEOUT_MS = 20_000;

function outcomeOf(result: NamedSharesResult): 'ok' | 'refused' | 'error' | 'cancelled' {
  if (result.ok) return 'ok';
  const { category } = result.error;
  if (category === 'cancelled') return 'cancelled';
  return category === 'authorization' || category === 'authentication' || category === 'not_found' || category === 'invalid_input' || category === 'conflict' ? 'refused' : 'error';
}

async function send(connectionId: string, action: StorageNamedSharesAction): Promise<NamedSharesResult> {
  const api = window.electronAPI?.azureStorage;
  if (!api?.namedShares) {
    const error: StorageOperationError = { category: 'unsupported', retryable: false, message: 'Named shares need the desktop app.' };
    return { ok: false, error };
  }
  return api.namedShares({ connectionId, requestId: uuidv4(), timeoutMs: NAMED_SHARES_TIMEOUT_MS, action });
}

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((name, index) => name === b[index]);

/** Re-read account info whenever the answer may have moved the folder: `data-plane` never changes source. */
async function refreshAccountInfo(connectionId: string, answer: StorageNamedSharesAnswer): Promise<void> {
  if (answer.source === 'data-plane') return;
  await reprobeStorageAccountInfo(connectionId, useStorageAccountInfoStore.getState().byConnection[connectionId]?.generation ?? 0);
}

/** Persist exactly the answer's set (never a draft), then refresh the folder state. */
export async function applyNamedSharesAnswer(connectionId: string, answer: StorageNamedSharesAnswer): Promise<void> {
  const store = useConnectionStore.getState();
  const saved = store.connections.find((connection) => connection.id === connectionId);
  if (saved && !sameList(saved.azureStorageNamedShares ?? [], answer.shares)) {
    await store.updateConnection({ ...saved, azureStorageNamedShares: answer.shares.length > 0 ? answer.shares : undefined });
  }
  await refreshAccountInfo(connectionId, answer);
}

async function runTracked(connectionId: string, action: StorageNamedSharesAction, event: 'open_by_name' | 'named_share_remove', source: ShareActionSource): Promise<NamedSharesResult> {
  const result = await send(connectionId, action);
  if (result.ok) await applyNamedSharesAnswer(connectionId, result.value);
  trackStorageEvent('feature:storage_share_action', { action: event, outcome: outcomeOf(result), source, protocol: 'unknown' });
  return result;
}

/** "Open share by name…": main verifies the share root; a refusal changes nothing and comes back as the typed error. */
export function openShareByName(connectionId: string, share: string, source: ShareActionSource): Promise<NamedSharesResult> {
  return runTracked(connectionId, { kind: 'add', share: share.trim() }, 'open_by_name', source);
}

/** "Remove from this connection" on a named share node (no request to Azure). */
export function removeNamedShare(connectionId: string, share: string, source: ShareActionSource): Promise<NamedSharesResult> {
  return runTracked(connectionId, { kind: 'remove', share }, 'named_share_remove', source);
}

/**
 * The connected connection form's save path (§3.2): replace the live set BEFORE the field is persisted, then persist
 * the answer's `shares`; each refusal comes back for "Not added: <name> — <message>". A form field: no event.
 */
export async function replaceNamedShares(connectionId: string, shares: string[]): Promise<NamedSharesResult> {
  const result = await send(connectionId, { kind: 'replace', shares });
  if (result.ok) await refreshAccountInfo(connectionId, result.value); // the form's own save persists `shares`
  return result;
}

export function notAddedLines(answer: StorageNamedSharesAnswer): string[] {
  return (answer.refused ?? []).map(({ share, error }) => `Not added: ${share} — ${error.message}`);
}
